import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, ChevronLeft, ChevronRight, X, Swords, Trophy, Flag, Hourglass, Zap,
  UserCircle2, Users, Crown, Rocket, LayoutDashboard, Building2,
  HelpCircle, Activity, ShieldAlert, Calendar as CalendarIcon,
  ArrowRight,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type Competition,
  type MatchSummary,
  type Me,
  type Role,
} from '../lib/api';

type BrowseClanEntry = { name: string; description?: string; leaderId: string; memberCount: number };
import { useViewAs } from '../lib/viewAs';
import { canRegisterClan } from '../lib/roles';
import { cn } from '../lib/cn';
import { clanLabelFrom } from '../lib/displayName';
import { divisionLetter } from '../lib/groupMatchesByStage';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';

// ─────────────────────────────────────────────────────────────────────────────
// Command Center — single entry point for all roles.
//
// Centre of the portal. Calendar-led, role-graduated. Public reads, member
// joins, leader schedules, admin composes, founder flips orgs live.
//
// Design law: ≤5 primary CTAs per view. Day-cells and drill-ins don't count.
// See docs/planning (or the 2026-04-19 session transcript) for the full plan.
// ─────────────────────────────────────────────────────────────────────────────

type EventKind =
  | 'match'
  | 'comp-signup-open'
  | 'comp-signup-close'
  | 'comp-start'
  | 'comp-end'
  | 'comp-seeding-deadline';

interface CalEvent {
  id: string;
  kind: EventKind;
  ts: number;
  title: string;
  subtitle?: string;
  compId?: string;
  compName?: string;
  matchId?: string;
  status?: string;
  href?: string;
  personal?: boolean;
  // Division-stage matches only (generateDivisionsPlay's groupIndex) — the
  // NIGHT VIEW (DaySheet) groups match events by division when present,
  // falling back to grouping by compName otherwise. See divisionLetter's
  // doc comment (lib/groupMatchesByStage.ts) — same letter mapping every
  // other division-tagged surface (Matches.tsx) uses, so this view can never
  // drift from what "Division A/B/…" means elsewhere.
  groupIndex?: number;
}

// Monochrome palette — icons carry category, colour only encodes "yours"/"urgent".
const KIND_META: Record<EventKind, { label: string; icon: typeof Swords }> = {
  'match':                  { label: 'Match',          icon: Swords    },
  'comp-signup-open':       { label: 'Sign-up opens',  icon: Flag      },
  'comp-signup-close':      { label: 'Sign-up closes', icon: Flag      },
  'comp-start':             { label: 'Comp start',     icon: Zap       },
  'comp-end':               { label: 'Comp end',       icon: Trophy    },
  'comp-seeding-deadline':  { label: 'Seeding close',  icon: Hourglass },
};

// ── helpers ───────────────────────────────────────────────────────────────────

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayKey(d: Date) { return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; }
function formatMonth(d: Date) { return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }); }
function formatFullDate(d: Date) { return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
function formatTime(ts: number) { return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
function stripClanPrefix(s: string | undefined) { return (s || '').replace(/^CLAN#/, ''); }

function matchTs(m: MatchSummary): number {
  return (m as unknown as { scheduledAt?: number }).scheduledAt
      ?? (m as unknown as { matchEndTime?: number }).matchEndTime
      ?? (m as unknown as { createdAt?: number }).createdAt
      ?? 0;
}

function buildCompEvents(comp: Competition): CalEvent[] {
  const events: CalEvent[] = [];
  const compHref = `/matches?compId=${encodeURIComponent(comp.id)}`;
  const sOpen = (comp as unknown as { signUpOpensAt?: number }).signUpOpensAt;
  const sClose = (comp as unknown as { signUpClosesAt?: number }).signUpClosesAt;
  const sStart = (comp as unknown as { startTime?: number }).startTime;
  const sEnd = (comp as unknown as { endTime?: number }).endTime;
  const seed = comp.seedingDeadline;
  if (sOpen) events.push({ id: `${comp.id}-su-open`, kind: 'comp-signup-open', ts: sOpen, title: comp.name, subtitle: 'Sign-up opens', compId: comp.id, href: compHref });
  if (sClose) events.push({ id: `${comp.id}-su-close`, kind: 'comp-signup-close', ts: sClose, title: comp.name, subtitle: 'Sign-up closes', compId: comp.id, href: compHref });
  if (sStart) events.push({ id: `${comp.id}-start`, kind: 'comp-start', ts: sStart, title: comp.name, subtitle: 'Competition starts', compId: comp.id, href: compHref });
  if (sEnd) events.push({ id: `${comp.id}-end`, kind: 'comp-end', ts: sEnd, title: comp.name, subtitle: 'Competition ends', compId: comp.id, href: compHref });
  if (seed) events.push({ id: `${comp.id}-seed`, kind: 'comp-seeding-deadline', ts: seed, title: comp.name, subtitle: 'Seeding deadline', compId: comp.id, href: compHref });
  return events;
}

function buildMatchEvents(compId: string, compName: string, matches: MatchSummary[], myClanName?: string | null): CalEvent[] {
  return matches
    .filter(m => matchTs(m) > 0)
    .map(m => {
      const c1 = stripClanPrefix(m.clan1);
      const c2 = stripClanPrefix(m.clan2);
      // personal MUST compare canonical forms — myClanName (me.clanName) is
      // always the bare-slug identity, never a display name. Only the tile
      // title itself prefers the denormalized display name (Lisa,
      // 2026-07-13, clan display-name-preservation fix).
      const personal = !!myClanName && (c1 === myClanName || c2 === myClanName);
      const c1Label = clanLabelFrom(m.clan1DisplayName, m.clan1);
      const c2Label = clanLabelFrom(m.clan2DisplayName, m.clan2);
      return {
        id: `${compId}::${m.sk}`,
        kind: 'match' as const,
        ts: matchTs(m),
        title: `${c1Label} vs ${c2Label}`,
        subtitle: m.status,
        compId,
        compName,
        matchId: m.sk,
        status: m.status,
        href: `/card/${encodeURIComponent(m.sk)}?compId=${encodeURIComponent(compId)}`,
        personal,
        groupIndex: m.groupIndex,
      };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export function CommandCenter() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { viewAs } = useViewAs();
  const effectiveRole: Role = (viewAs?.role ?? me?.role ?? 'public') as Role;

  const { data: comps = [], isLoading: compsLoading } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.competitions,
  });

  // Fan-out to pull matches per comp. Each query is independent — atomic, parallel.
  const matchQueries = useQueries({
    queries: comps.map(c => ({
      queryKey: ['matches', c.id],
      queryFn: () => api.matches(c.id),
      enabled: !!c.id,
    })),
  });

  const matchEventsByComp: CalEvent[] = useMemo(() => {
    const out: CalEvent[] = [];
    matchQueries.forEach((q, i) => {
      if (!q.data) return;
      out.push(...buildMatchEvents(comps[i].id, comps[i].name, q.data, me?.clanName));
    });
    return out;
  }, [matchQueries, comps, me?.clanName]);

  const compEvents: CalEvent[] = useMemo(() => comps.flatMap(buildCompEvents), [comps]);

  const allEvents: CalEvent[] = useMemo(
    () => [...matchEventsByComp, ...compEvents].sort((a, b) => a.ts - b.ts),
    [matchEventsByComp, compEvents]
  );

  // ── Calendar state ──
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // ── Search state ──
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  if (compsLoading) return <PageSpinner />;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 p-4 pb-52 sm:p-6 md:pb-32">
      <div className={SETTLE} style={settleDelay(0)}>
        <Header me={me} effectiveRole={effectiveRole} />
      </div>

      {effectiveRole !== 'public' && (
        <div className={SETTLE} style={settleDelay(1)}>
          <GlobalSearch
            query={searchQuery}
            onQuery={setSearchQuery}
            focused={searchFocused}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 120)}
            comps={comps}
          />
        </div>
      )}

      {effectiveRole === 'public' && (
        <div className={SETTLE} style={settleDelay(1)}>
          <PublicCompsPanel comps={comps} />
        </div>
      )}

      <div className={SETTLE} style={settleDelay(2)}>
        <TodayStrip events={allEvents} role={effectiveRole} isAuthed={!!me} />
      </div>

      <div className={SETTLE} style={settleDelay(3)}>
        <MonthCalendar
          cursor={cursor}
          onCursor={setCursor}
          events={allEvents}
          onSelectDay={setSelectedDay}
          selectedDay={selectedDay}
        />
      </div>

      {selectedDay && (
        <DaySheet
          day={selectedDay}
          events={allEvents}
          role={effectiveRole}
          me={me}
          onClose={() => setSelectedDay(null)}
        />
      )}

      <CTABar role={effectiveRole} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────

function Header({ me, effectiveRole }: { me: Me | undefined; effectiveRole: Role }) {
  const avatar = me?.discordProfile?.avatar;
  const clanTag = me?.discordProfile?.discordClanTag;
  // Opt-in to per-user accent colour (set on document root by AuthGate).
  // Falls through to forge orange if user hasn't set a Discord accent or
  // the colour failed the contrast-safety check.
  const userAccent = 'var(--accent-user, var(--forge-500))';

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {avatar && me?.discordId && (
          <img
            src={`https://cdn.discordapp.com/avatars/${me.discordId}/${avatar}.png?size=64`}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full ring-2"
            style={{ borderColor: userAccent, '--tw-ring-color': userAccent } as React.CSSProperties}
          />
        )}
        <div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
            Command Center
          </h1>
          <p className="mt-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-meta">
            {me?.displayName && (
              <span style={{ color: userAccent }}>{me.displayName}</span>
            )}
            {clanTag && (
              <span className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-ink-secondary">
                {clanTag}
              </span>
            )}
            <span>· {effectiveRole}</span>
          </p>
        </div>
      </div>
      <div className="hidden items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ink-secondary sm:flex">
        <CalendarIcon className="h-3 w-3" strokeWidth={3} />
        Phase 1 · read-only
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Global search (clans + comps; player search is Phase 2)
// ─────────────────────────────────────────────────────────────────────────────

function GlobalSearch({
  query, onQuery, focused, onFocus, onBlur, comps,
}: {
  query: string;
  onQuery: (s: string) => void;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  comps: Competition[];
}) {
  const { data: browseData } = useQuery({
    queryKey: ['browseClans'],
    queryFn: api.browseClans,
    staleTime: 60_000,
  });
  const clans: BrowseClanEntry[] = browseData?.clans ?? [];

  const q = query.trim().toLowerCase();
  const matchedClans = q ? clans.filter(c => c.name.toLowerCase().includes(q)).slice(0, 8) : [];
  const matchedComps = q ? comps.filter(c => c.name.toLowerCase().includes(q)).slice(0, 5) : [];
  const showDropdown = focused && q.length > 0;
  const hasResults = matchedClans.length + matchedComps.length > 0;

  return (
    <div className="relative">
      <div className="flex items-center gap-3 rounded-lg bg-paper-inset px-4 py-3">
        <Search className="h-4 w-4 shrink-0 text-ink-meta" strokeWidth={2.5} />
        <input
          value={query}
          onChange={e => onQuery(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="Search clans, comps…"
          className="flex-1 bg-transparent font-mono text-sm text-white placeholder:text-ink-meta focus:outline-none"
        />
        {query && (
          <button
            onClick={() => onQuery('')}
            className="text-ink-meta hover:text-ink-primary"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-96 overflow-y-auto rounded-lg bg-paper-panel shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
          {!hasResults && (
            <p className="px-4 py-6 text-center font-mono text-xs uppercase tracking-widest text-ink-meta">
              No matches for “{query}”
            </p>
          )}
          {matchedComps.length > 0 && (
            <div>
              <p className="px-4 pb-1.5 pt-3 font-display text-[9px] font-black uppercase tracking-[0.25em] text-ink-meta">Competitions</p>
              {matchedComps.map(c => (
                <Link
                  key={c.id}
                  to={`/matches?compId=${encodeURIComponent(c.id)}`}
                  className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-white/[0.04]"
                  onMouseDown={e => e.preventDefault()}
                >
                  <Trophy className="h-4 w-4 text-ink-secondary" strokeWidth={2.5} />
                  <div className="flex-1">
                    <p className="font-display text-sm font-black uppercase tracking-wider text-white">{c.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">{c.status}</p>
                  </div>
                  <ArrowRight className="h-3 w-3 text-ink-meta" />
                </Link>
              ))}
            </div>
          )}
          {matchedClans.length > 0 && (
            <div>
              <p className="px-4 pb-1.5 pt-3 font-display text-[9px] font-black uppercase tracking-[0.25em] text-ink-meta">Clans</p>
              {matchedClans.map(c => (
                <Link
                  key={c.name}
                  to={`/clan/${encodeURIComponent(c.name)}`}
                  className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-white/[0.04]"
                  onMouseDown={e => e.preventDefault()}
                >
                  <Users className="h-4 w-4 text-ink-secondary" strokeWidth={2.5} />
                  <div className="flex-1">
                    <p className="font-display text-sm font-black uppercase tracking-wider text-white">{c.name}</p>
                    {c.memberCount !== undefined && (
                      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">{c.memberCount} members</p>
                    )}
                  </div>
                  <ArrowRight className="h-3 w-3 text-ink-meta" />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Today Strip — hybrid: personal above, feed below
// ─────────────────────────────────────────────────────────────────────────────

function TodayStrip({ events, role, isAuthed }: { events: CalEvent[]; role: Role; isAuthed: boolean }) {
  const today = useMemo(() => new Date(), []);
  const startOfDay = useMemo(() => { const d = new Date(today); d.setHours(0,0,0,0); return d.getTime(); }, [today]);
  const endOfDay   = useMemo(() => { const d = new Date(today); d.setHours(23,59,59,999); return d.getTime(); }, [today]);

  const todayEvents = events.filter(e => e.ts >= startOfDay && e.ts <= endOfDay);
  const personal = todayEvents.filter(e => e.personal);
  const feed = todayEvents.filter(e => !e.personal).slice(0, 5);

  const isPublic = role === 'public';

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="border-none bg-paper-elevated shadow-none">
        <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.25em] text-ink-meta">
          {isPublic ? 'Welcome to GG Anvil' : 'Your day'}
        </p>
        {isPublic ? (
          isAuthed ? (
            // Signed in, but role resolves to `public` — a Discord user with no
            // clan/comp yet, or a founder previewing View-As → public. The real
            // logged-out state never reaches CommandCenter (the root is behind
            // AuthGate → /expired), so a "Log in with Discord" button here would
            // just tell an already-signed-in user to sign in. Show a real next
            // step instead. (Lisa, 2026-07-17.)
            <div>
              <p className="font-display text-sm font-bold text-white">
                You're signed in — you're just not in a clan or competition yet.
              </p>
              <p className="mt-1.5 font-mono text-xs leading-relaxed text-ink-body">
                Join a clan or competition and your matches, schedule, and
                standings show up right here.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/clans/browse" className={cn('btn-primary inline-flex items-center gap-2', MORPH)}>
                  <Users className="h-3.5 w-3.5" strokeWidth={2.5} /> Browse clans
                </Link>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center gap-2 rounded-md bg-white/[0.05] px-3 py-2 text-xs text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary"
                >
                  How it works
                </Link>
              </div>
            </div>
          ) : (
            // Truly anonymous (no session). Unreachable today — the root sits
            // behind AuthGate — but kept correct so the card still makes sense
            // if a logged-out public-browse surface is ever switched on.
            <div>
              <p className="font-display text-sm font-bold text-white">
                The tournament portal for GG Anvil clans.
              </p>
              <p className="mt-1.5 font-mono text-xs leading-relaxed text-ink-body">
                See your matches, join a competition, and track standings.
                Sign in with Discord to get your personalised schedule.
              </p>
              <button
                onClick={() => {
                  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                  window.location.href = `/auth/discord/start?returnTo=${returnTo}`;
                }}
                className={cn('btn-primary mt-3 inline-flex items-center gap-2', MORPH)}
              >
                <Rocket className="h-3.5 w-3.5" strokeWidth={2.5} /> Log in with Discord
              </button>
            </div>
          )
        ) : personal.length === 0 ? (
          <p className="font-mono text-xs text-ink-meta">
            Nothing on the anvil for you today.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {personal.map(e => (
              <EventRow key={e.id} event={e} showTime />
            ))}
          </ul>
        )}
      </Card>

      <Card className="border-none bg-paper-elevated shadow-none">
        <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.25em] text-ink-meta">
          Today in GG Anvil
        </p>
        {feed.length === 0 ? (
          <p className="font-mono text-xs text-ink-meta">
            Nothing scheduled across the platform today.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {feed.map(e => (
              <EventRow key={e.id} event={e} showTime />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function EventRow({ event, showTime }: { event: CalEvent; showTime?: boolean }) {
  const meta = KIND_META[event.kind];
  const Icon = meta.icon;
  const content = (
    <div className="flex items-center gap-2.5 rounded-md bg-white/[0.04] px-2.5 py-1.5 transition-colors hover:bg-white/[0.07]">
      <Icon className={cn('h-3.5 w-3.5 shrink-0', event.personal ? 'text-emerald-300' : 'text-ink-secondary')} strokeWidth={2.5} />
      <span className="min-w-0 flex-1 truncate font-display text-xs font-bold uppercase tracking-wider text-white">
        {event.title}
      </span>
      {event.subtitle && (
        <span className="hidden font-mono text-[9px] uppercase tracking-widest text-ink-meta sm:inline">
          {event.subtitle}
        </span>
      )}
      {showTime && (
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-secondary">
          {formatTime(event.ts)}
        </span>
      )}
    </div>
  );
  return (
    <li>
      {event.href ? (
        <Link to={event.href} className="block">{content}</Link>
      ) : content}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Month calendar
// ─────────────────────────────────────────────────────────────────────────────

function MonthCalendar({
  cursor, onCursor, events, onSelectDay, selectedDay,
}: {
  cursor: Date;
  onCursor: (d: Date) => void;
  events: CalEvent[];
  onSelectDay: (d: Date) => void;
  selectedDay: Date | null;
}) {
  const first = startOfMonth(cursor);
  // Snap to previous Monday (week starts Mon in GB/SA)
  const gridStart = new Date(first);
  const offsetBack = (first.getDay() + 6) % 7; // Mon=0, Sun=6
  gridStart.setDate(first.getDate() - offsetBack);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  // Bucket events by day
  const buckets = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of events) {
      const d = new Date(e.ts);
      const k = dayKey(d);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return m;
  }, [events]);

  const today = new Date();

  return (
    <Card className="border-none bg-paper-elevated p-4 shadow-none sm:p-6">
      {/* Header row */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => onCursor(startOfMonth(new Date()))}
          className={cn('rounded-md bg-white/[0.05] px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-[0.2em] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
        >
          Today
        </button>
        {/* Month name + its OWN prev/next arrows — the arrows used to sit on the
            far left next to "Today", disconnected from the month they change;
            flanking the name makes the control obvious (Lisa, 2026-07-19). */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onCursor(addMonths(cursor, -1))}
            className={cn('flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <h2 className="min-w-[9.5rem] text-center font-display text-lg font-black uppercase tracking-wider text-white sm:text-xl">
            {formatMonth(cursor)}
          </h2>
          <button
            onClick={() => onCursor(addMonths(cursor, 1))}
            className={cn('flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
        <div className="hidden items-center gap-3 font-mono text-[9px] uppercase tracking-widest text-ink-meta sm:flex">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-ink-secondary" />
            On the anvil
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            You're in it
          </span>
        </div>
      </div>

      {/* Day-of-week header — md+ only (agenda view replaces grid below md) */}
      <div className="mb-1 hidden grid-cols-7 gap-1 md:grid">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
          <div key={d} className="px-2 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-ink-meta">
            {d}
          </div>
        ))}
      </div>

      {/* Agenda view — below md only. Stacked list of days-with-events. */}
      <AgendaView cursor={cursor} buckets={buckets} today={today} onSelectDay={onSelectDay} />

      {/* Grid — md+ only */}
      <div className="hidden grid-cols-7 gap-1 md:grid">
        {cells.map(d => {
          const k = dayKey(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = selectedDay && sameDay(d, selectedDay);
          const dayEvents = buckets.get(k) ?? [];
          return (
            <button
              key={k}
              onClick={() => onSelectDay(d)}
              // data-track pins the clickstream label to a stable, generic
              // name — without it resolveLabel (lib/clickstream.ts) falls
              // through to this cell's full textContent, which concatenates
              // the day number with every event title stacked inside it into
              // one garbled string (Lisa, 2026-07-21 audit).
              data-track="calendar-day"
              className={cn(
                'group relative flex min-h-[80px] flex-col gap-1 rounded-md bg-white/[0.03] p-1.5 text-left transition-colors sm:min-h-[110px]',
                !inMonth && 'opacity-40',
                'hover:bg-white/[0.06]',
                isSelected && 'ring-2 ring-accent-500',
                isToday && !isSelected && 'ring-1 ring-white/20',
              )}
            >
              <span className={cn(
                'font-display text-xs font-black',
                isToday ? 'text-accent-500' : 'text-ink-secondary'
              )}>
                {d.getDate()}
              </span>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map(e => (
                  <div
                    key={e.id}
                    className={cn(
                      'truncate rounded-sm bg-white/[0.05] px-1 py-0.5 font-mono text-[9px] uppercase tracking-wider',
                      e.personal ? 'text-emerald-200' : 'text-ink-secondary',
                    )}
                    title={e.title}
                  >
                    {e.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="font-mono text-[9px] font-bold uppercase tracking-widest text-ink-meta">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agenda view — mobile (below md). Replaces the unusable 7-col grid at ~390px.
// Vertical list of days-WITH-events in the visible month; days without events
// are omitted. Day header is tappable (opens DaySheet, same as a grid cell);
// each match/event row navigates exactly like the grid entry (EventRow Link).
// ─────────────────────────────────────────────────────────────────────────────

function AgendaView({
  cursor, buckets, today, onSelectDay,
}: {
  cursor: Date;
  buckets: Map<string, CalEvent[]>;
  today: Date;
  onSelectDay: (d: Date) => void;
}) {
  // Days of the cursor month that have at least one event, in date order.
  const days = useMemo(() => {
    const out: { date: Date; events: CalEvent[] }[] = [];
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const evs = buckets.get(dayKey(date));
      if (evs && evs.length > 0) {
        out.push({ date, events: [...evs].sort((a, b) => a.ts - b.ts) });
      }
    }
    return out;
  }, [cursor, buckets]);

  return (
    <div className="md:hidden">
      {days.length === 0 ? (
        <p className="rounded-lg bg-white/[0.04] px-4 py-8 text-center font-mono text-xs uppercase tracking-widest text-ink-meta">
          Nothing on the anvil this month.
        </p>
      ) : (
        <div className="space-y-4">
          {days.map(({ date, events }) => {
            const isToday = sameDay(date, today);
            const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
            return (
              <div key={dayKey(date)}>
                <button
                  onClick={() => onSelectDay(date)}
                  // Same garbled-textContent risk as the month-grid day cell
                  // above (weekday + date + "Today" badge + item count all
                  // concatenate) — same data-track fix, swept 2026-07-21.
                  data-track="calendar-day"
                  className={cn(
                    'mb-1.5 flex w-full items-center gap-2 text-left',
                    isToday ? 'text-accent-500' : 'text-ink-secondary',
                  )}
                >
                  <span className="font-display text-sm font-black uppercase tracking-wider">
                    {weekday} {date.getDate()}
                  </span>
                  {isToday && (
                    <span className="rounded-sm bg-white/[0.06] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-widest text-accent-500">
                      Today
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-ink-meta">
                    {events.length} {events.length === 1 ? 'item' : 'items'}
                  </span>
                </button>
                <ul className="space-y-1">
                  {events.map(e => (
                    <EventRow key={e.id} event={e} showTime />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Night view — DaySheet's match grouping (Lisa, 2026-07-19). The day drill-in
// used to be one flat chronological list; a busy division night (several
// divisions all playing the same evening) made that list unreadable — which
// division is which was buried in each row's title. Grouped instead: division
// matches bucket by "Division A/B/…" (divisionLetter — same letter mapping
// Matches.tsx already tags rows with, so this view can never disagree with
// that one); everything else (round robin, bracket, legacy, manually-created)
// buckets by competition name. Non-match CalEvent kinds (comp sign-up/
// start/end/seeding-deadline) are NOT part of this grouping — they keep
// rendering exactly as before, in their own "Other" list below the groups.
// ─────────────────────────────────────────────────────────────────────────────

interface NightMatchGroup {
  key: string;
  label: string;
  matches: CalEvent[];
}

function groupNightMatches(matchEvents: CalEvent[]): NightMatchGroup[] {
  const groups = new Map<string, NightMatchGroup>();
  for (const e of matchEvents) {
    const isDivision = typeof e.groupIndex === 'number';
    const key = isDivision ? `div-${e.groupIndex}` : `comp-${e.compId ?? 'unknown'}`;
    const label = isDivision ? `Division ${divisionLetter(e.groupIndex!)}` : (e.compName ?? 'Matches');
    let group = groups.get(key);
    if (!group) {
      group = { key, label, matches: [] };
      groups.set(key, group);
    }
    group.matches.push(e);
  }
  // Groups themselves stay chronological — earliest kickoff leads — while
  // each group's own matches keep the ascending order they arrived in
  // (dayEvents is already sorted by ts before this runs).
  return [...groups.values()].sort((a, b) => a.matches[0].ts - b.matches[0].ts);
}

function MatchNightRow({ event }: { event: CalEvent }) {
  const content = (
    <div className="flex items-center gap-2.5 rounded-md bg-white/[0.04] px-2.5 py-1.5 transition-colors hover:bg-white/[0.07]">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-ink-secondary">
        {formatTime(event.ts)}
      </span>
      <span className={cn('min-w-0 flex-1 truncate font-display text-xs font-bold uppercase tracking-wider', event.personal ? 'text-emerald-300' : 'text-white')}>
        {event.title}
      </span>
      {event.status && (
        <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-ink-meta">
          {event.status}
        </span>
      )}
    </div>
  );
  return (
    <li>
      {event.href ? <Link to={event.href} className="block">{content}</Link> : content}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Day Sheet (right drawer)
// ─────────────────────────────────────────────────────────────────────────────

function DaySheet({
  day, events, role, me, onClose,
}: {
  day: Date;
  events: CalEvent[];
  role: Role;
  me: Me | undefined;
  onClose: () => void;
}) {
  const start = new Date(day); start.setHours(0,0,0,0);
  const end = new Date(day); end.setHours(23,59,59,999);
  const dayEvents = events
    .filter(e => e.ts >= start.getTime() && e.ts <= end.getTime())
    .sort((a, b) => a.ts - b.ts);

  // Night view: matches group by division/comp (see groupNightMatches' doc
  // comment above); every other CalEvent kind keeps its old flat rendering.
  const matchEvents = dayEvents.filter(e => e.kind === 'match');
  const otherEvents = dayEvents.filter(e => e.kind !== 'match');
  const nightGroups = groupNightMatches(matchEvents);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/70"
        aria-hidden
      />
      {/* Drawer */}
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-paper-panel shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
        <div className="flex items-start justify-between px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-meta">Day sheet</p>
            <h3 className="mt-1 font-display text-lg font-black uppercase tracking-wider text-white">
              {formatFullDate(day)}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={cn('flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {dayEvents.length === 0 ? (
            <p className="py-6 text-center font-mono text-xs uppercase tracking-widest text-ink-meta">
              Nothing on the anvil this day.
            </p>
          ) : (
            <div className="space-y-5">
              {nightGroups.map(group => (
                <div key={group.key}>
                  <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-ink-meta">
                    {group.label}
                  </p>
                  <ul className="space-y-1.5">
                    {group.matches.map(e => (
                      <MatchNightRow key={e.id} event={e} />
                    ))}
                  </ul>
                </div>
              ))}
              {otherEvents.length > 0 && (
                <div>
                  {nightGroups.length > 0 && (
                    <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-ink-meta">
                      Other
                    </p>
                  )}
                  <ul className="space-y-1.5">
                    {otherEvents.map(e => (
                      <EventRow key={e.id} event={e} showTime />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <DayActions day={day} role={role} me={me} onClose={onClose} />
          </div>
        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Day Actions — role-gated composer surface. Each branch is self-contained.
// ─────────────────────────────────────────────────────────────────────────────

function DayActions({
  day, role, me, onClose,
}: {
  day: Date;
  role: Role;
  me: Me | undefined;
  onClose: () => void;
}) {
  const hasClan = !!me?.clanName;

  if (role === 'public') {
    return (
      <ActionPanel title="Want in?">
        <p className="font-mono text-xs text-iron-400">
          Active comps are listed at the top of the page — click one to apply as a clan owner or join an existing clan.
        </p>
        <button
          onClick={onClose}
          className={cn('btn-primary mt-3 inline-flex items-center gap-2', MORPH)}
        >
          <Rocket className="h-3.5 w-3.5" strokeWidth={2.5} /> Got it
        </button>
      </ActionPanel>
    );
  }

  if (role === 'member' && !hasClan) {
    return (
      <ActionPanel title="Join a clan">
        <p className="mb-3 font-mono text-xs text-iron-400">
          Pick one and apply. Leaders decide who's in.
        </p>
        <JoinClanInline onClose={onClose} />
      </ActionPanel>
    );
  }

  if (role === 'leader' && hasClan) {
    return (
      <ActionPanel title="Schedule a match">
        <ScheduleMatchInline day={day} myClan={me!.clanName!} onDone={onClose} />
      </ActionPanel>
    );
  }

  if (role === 'admin' || role === 'founder') {
    return (
      <ActionPanel title="Create a match">
        <CreateMatchInline day={day} onDone={onClose} />
      </ActionPanel>
    );
  }

  // Member with a clan, no composer for Phase 2 — link to their clan.
  return (
    <ActionPanel title="Your stuff">
      <div className="flex flex-col gap-2">
        <Link
          to="/player/me"
          onClick={onClose}
          className={cn('flex items-center gap-2 rounded-lg bg-white/[0.05] px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
        >
          <UserCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} /> My card
        </Link>
        {me?.clanName && (
          <Link
            to={`/clan/${encodeURIComponent(me.clanName)}`}
            onClick={onClose}
            className={cn('flex items-center gap-2 rounded-lg bg-white/[0.05] px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
          >
            <Crown className="h-3.5 w-3.5" strokeWidth={2.5} /> My clan — {me.clanName}
          </Link>
        )}
      </div>
    </ActionPanel>
  );
}

function ActionPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-white/[0.04] p-4">
      <p className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.25em] text-ink-meta">
        {title}
      </p>
      {children}
    </div>
  );
}

// ── Join Clan (inline) ──────────────────────────────────────────────────────

function PublicCompsPanel({ comps }: { comps: Competition[] }) {
  const [selectedComp, setSelectedComp] = useState<{ id: string; name: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const liveComps = comps;

  // Deep-link support: the public org/comp page's "Register" button sends
  // visitors here as `/?join=<compId>` — open the join modal for that comp
  // immediately instead of leaving the link a silent no-op. Falls back to
  // the raw id (sans COMP# prefix) as the display name if the comp isn't in
  // the currently-visible list, then scrubs the param off the URL so a
  // refresh/back-nav doesn't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const joinId = searchParams.get('join');
    if (!joinId) return;
    const match = liveComps.find(c => c.id === joinId);
    setSelectedComp({ id: joinId, name: match?.name ?? joinId.replace(/^COMP#/, '') });
    const next = new URLSearchParams(searchParams);
    next.delete('join');
    setSearchParams(next, { replace: true });
    // Deep-link is consumed once on mount — deliberately not re-run on comps
    // updates (the modal, once open, owns its own lifecycle via onClose).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cap the active-comps list so it can't grow unbounded (founder feedback).
  const PAGE = 9;
  const visibleComps = showAll ? liveComps : liveComps.slice(0, PAGE);
  const hiddenCount = liveComps.length - visibleComps.length;

  return (
    <>
      <div className="rounded-lg bg-paper-elevated p-4">
        <p className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.25em] text-ink-meta">
          Active competitions
        </p>
        {liveComps.length === 0 ? (
          <p className="font-mono text-xs text-ink-meta">
            No active comps right now. Check back soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {visibleComps.map(c => {
              const cardCls = cn('group flex flex-col items-start gap-1 rounded-lg bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.07]', MORPH);
              // Every comp now resolves to a working /comp/:id page — the
              // data-driven one if authored, else the neutral fallback — so
              // "View page" always works. NOTE: Competition.publicPageEnabled
              // exists but is a DIFFERENT, unrelated toggle (the opt-in
              // /watch/:compId spectator page flipped via PublicPageToggle);
              // gating on it here would incorrectly hide "View page" for any
              // comp that hasn't opted into spectator mode. Always true.
              const hasPage = true;
              const inner = (
                <>
                  <span className="font-display text-sm font-black uppercase tracking-wider text-white">
                    {c.name}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">
                    {c.status ?? '—'} · {c.stage ?? '—'}
                  </span>
                  <span className="mt-1 inline-flex items-center gap-1 font-display text-[10px] font-black uppercase tracking-widest text-forge-500">
                    {hasPage ? 'View page' : 'Join'} <ArrowRight className="h-3 w-3" />
                  </span>
                </>
              );
              return hasPage ? (
                <Link key={c.id} to={`/comp/${encodeURIComponent(c.id)}`} className={cardCls}>
                  {inner}
                </Link>
              ) : (
                <button key={c.id} onClick={() => setSelectedComp({ id: c.id, name: c.name })} className={cardCls}>
                  {inner}
                </button>
              );
            })}
          </div>
        )}
        {hiddenCount > 0 && (
          <button
            onClick={() => setShowAll(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink-secondary transition-colors hover:bg-white/[0.07] hover:text-ink-primary"
          >
            Show {hiddenCount} more
          </button>
        )}
      </div>
      {selectedComp && (
        <JoinCompModal comp={selectedComp} onClose={() => setSelectedComp(null)} />
      )}
    </>
  );
}

function JoinCompModal({ comp, onClose }: { comp: { id: string; name: string }; onClose: () => void }) {
  const [stage, setStage] = useState<'choose' | 'apply-owner' | 'browse-clans' | 'submitted'>('choose');
  const [ownerMessage, setOwnerMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const MAX_MSG = 250;

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const myClanTag = me?.discordProfile?.discordClanTag;

  const clansQuery = useQuery({
    queryKey: ['public-comp-clans', comp.id],
    queryFn: () => api.competitionClanList(comp.id),
    enabled: stage === 'browse-clans',
  });

  // If the user has a Discord clan tag and a clan in this comp matches it,
  // we can offer to link them to that clan instead of (or as well as) the
  // free-form owner application.
  const matchedClan = myClanTag && clansQuery.data
    ? clansQuery.data.find(c => c.name.toLowerCase() === myClanTag.toLowerCase())
    : undefined;
  const applyClanMut = useMutation({
    mutationFn: (clanName: string) => api.applyToClan(clanName, {}),
  });
  const applyOwnerMut = useMutation({
    mutationFn: () => api.applyAsCompAdmin(comp.id, { message: ownerMessage }),
    onSuccess: () => setStage('submitted'),
    onError: (err: Error) => setErrorMsg(parseApiError(err).message),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 pt-12"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg bg-paper-panel p-5 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-ink-meta hover:text-ink-primary"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <p className="mb-1 font-display text-[10px] font-black uppercase tracking-[0.25em] text-ink-meta">
          Join
        </p>
        <p className="mb-4 font-display text-base font-black uppercase tracking-wider text-white">
          {comp.name}
        </p>

        {stage === 'choose' && (
          <div className="space-y-2">
            {/* Registration-hole closure (2026-07-17): the public "Register"
                button used to only offer comp-admin application / join-an-
                existing-clan — neither of which is the actual clan-leader
                self-serve flow (JoinCompScreen). A signed-in clan leader gets
                a direct door to it here instead of having to already know
                about /gate/join-comp. Gated the same way as every other
                doorway into that flow (lib/roles.ts's canRegisterClan). */}
            {canRegisterClan(me?.role, me?.clanName) && (
              <Link
                to={`/gate/join-comp?compId=${encodeURIComponent(comp.id)}`}
                onClick={onClose}
                className={cn('flex w-full items-start justify-between rounded-lg bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.07]', MORPH)}
              >
                <div>
                  <p className="font-display text-xs font-black uppercase tracking-wider text-white">
                    Register my clan
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] text-ink-meta">
                    You already lead a clan — enter it into this comp directly.
                  </p>
                </div>
                <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-meta" />
              </Link>
            )}
            <button
              onClick={() => setStage('apply-owner')}
              className={cn('flex w-full items-start justify-between rounded-lg bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.07]', MORPH)}
            >
              <div>
                <p className="font-display text-xs font-black uppercase tracking-wider text-white">
                  Apply as a clan owner
                </p>
                <p className="mt-0.5 font-mono text-[10px] text-ink-meta">
                  Send a personal note to the comp owner. They'll reach out if they want you on board.
                </p>
              </div>
              <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-meta" />
            </button>
            <button
              onClick={() => setStage('browse-clans')}
              className={cn('flex w-full items-start justify-between rounded-lg bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.07]', MORPH)}
            >
              <div>
                <p className="font-display text-xs font-black uppercase tracking-wider text-white">
                  Join an existing clan
                </p>
                <p className="mt-0.5 font-mono text-[10px] text-ink-meta">
                  Pick a clan in this comp and apply. Leaders decide.
                </p>
              </div>
              <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-meta" />
            </button>
          </div>
        )}

        {stage === 'apply-owner' && (
          <>
            <button onClick={() => setStage('choose')} className="mb-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-ink-meta hover:text-ink-secondary">
              <ChevronLeft className="h-3 w-3" /> Back
            </button>
            {myClanTag && (
              <p className="mb-3 rounded-lg bg-white/[0.04] px-3 py-2 font-mono text-[11px] text-ink-body">
                We see your Discord clan tag is <span className="font-bold uppercase tracking-wider text-white">{myClanTag}</span>.
                Mention it in your message — the comp owner can link you to that clan if it already exists.
              </p>
            )}
            <p className="mb-2 font-mono text-xs text-ink-secondary">
              Personal message to the comp owner / admins:
            </p>
            <textarea
              value={ownerMessage}
              onChange={e => setOwnerMessage(e.target.value.slice(0, MAX_MSG))}
              placeholder="Hi, I'd like to bring my clan into your comp because…"
              rows={4}
              className="input mb-1 w-full resize-y"
            />
            <p className="mb-3 text-right font-mono text-[10px] text-ink-meta">
              {ownerMessage.length}/{MAX_MSG}
            </p>
            {errorMsg && <p className="mb-2 text-[10px] text-red-400">{errorMsg}</p>}
            <button
              onClick={() => applyOwnerMut.mutate()}
              disabled={applyOwnerMut.isPending || ownerMessage.trim().length === 0}
              className={cn('btn-primary w-full disabled:opacity-50', MORPH)}
            >
              {applyOwnerMut.isPending ? 'Sending…' : 'Send application'}
            </button>
          </>
        )}

        {stage === 'browse-clans' && (
          <>
            <button onClick={() => setStage('choose')} className="mb-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-ink-meta hover:text-ink-secondary">
              <ChevronLeft className="h-3 w-3" /> Back
            </button>
            {matchedClan && (
              <p className="mb-3 rounded-lg bg-white/[0.04] px-3 py-2 font-mono text-[11px] text-emerald-200">
                <span className="font-bold uppercase tracking-wider">{matchedClan.name}</span> matches your Discord clan tag — apply there if it's the same clan.
              </p>
            )}
            <p className="mb-3 font-mono text-xs text-ink-secondary">
              Clans accepting applications:
            </p>
            {clansQuery.isLoading && <p className="text-xs text-ink-meta">Loading…</p>}
            {clansQuery.data && clansQuery.data.length === 0 && (
              <p className="text-xs text-ink-meta">No clans available in this comp yet.</p>
            )}
            <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
              {(clansQuery.data ?? []).map(c => (
                <li key={c.id} className="flex items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2">
                  <span className="min-w-0 flex-1 truncate font-display text-xs font-black uppercase tracking-wider text-white">
                    {c.name}
                  </span>
                  {applyClanMut.isSuccess && applyClanMut.variables === c.name ? (
                    <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">Applied</span>
                  ) : (
                    <button
                      onClick={() => applyClanMut.mutate(c.name)}
                      disabled={applyClanMut.isPending}
                      className={cn('btn-primary disabled:opacity-50', MORPH)}
                    >
                      Apply
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {applyClanMut.isError && (
              <p className="mt-2 text-[10px] text-red-400">{(applyClanMut.error as Error).message}</p>
            )}
          </>
        )}

        {stage === 'submitted' && (
          <>
            <p className="font-display text-xs font-black uppercase tracking-wider text-emerald-300">
              Application sent
            </p>
            <p className="mt-2 font-mono text-xs text-ink-secondary">
              The comp owner and admins for <span className="text-white">{comp.name}</span> have been notified.
              You'll get a Discord DM when they decide.
            </p>
            <button
              onClick={onClose}
              className={cn('btn-ghost mt-4 w-full', MORPH)}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function JoinClanInline({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['browseClans'], queryFn: api.browseClans, staleTime: 60_000 });
  const clans: BrowseClanEntry[] = data?.clans ?? [];
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState<Record<string, 'pending' | 'applied' | 'error'>>({});
  const [errorMsg, setErrorMsg] = useState<Record<string, string>>({});

  const apply = useMutation({
    mutationFn: ({ clanName }: { clanName: string }) => api.applyToClan(clanName, {}),
    onMutate: ({ clanName }) => setStatus(s => ({ ...s, [clanName]: 'pending' })),
    onSuccess: (_d, vars) => {
      setStatus(s => ({ ...s, [vars.clanName]: 'applied' }));
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: Error, vars) => {
      setStatus(s => ({ ...s, [vars.clanName]: 'error' }));
      setErrorMsg(m => ({ ...m, [vars.clanName]: parseApiError(err).message || 'Failed' }));
    },
  });

  const q = filter.trim().toLowerCase();
  const filtered = q ? clans.filter(c => c.name.toLowerCase().includes(q)) : clans;

  return (
    <>
      <div className="mb-3 flex items-center gap-2 rounded-lg bg-paper-inset px-3 py-2">
        <Search className="h-3.5 w-3.5 text-ink-meta" strokeWidth={2.5} />
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter clans…"
          className="flex-1 bg-transparent font-mono text-xs text-white placeholder:text-ink-meta focus:outline-none"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="py-4 text-center font-mono text-xs uppercase tracking-widest text-ink-meta">
          No clans match that filter.
        </p>
      ) : (
        <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
          {filtered.slice(0, 30).map(c => {
            const s = status[c.name];
            return (
              <li key={c.name} className="flex items-center gap-2 rounded-lg bg-white/[0.04] px-2.5 py-1.5">
                <Link
                  to={`/clan/${encodeURIComponent(c.name)}`}
                  onClick={onClose}
                  className="min-w-0 flex-1"
                >
                  <p className="truncate font-display text-xs font-black uppercase tracking-wider text-white hover:text-accent-500">
                    {c.name}
                  </p>
                  <p className="truncate font-mono text-[10px] uppercase tracking-widest text-ink-meta">
                    {c.memberCount} member{c.memberCount !== 1 ? 's' : ''}
                  </p>
                </Link>
                {s === 'applied' ? (
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
                    Applied
                  </span>
                ) : s === 'error' ? (
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-red-400" title={errorMsg[c.name]}>
                    Failed
                  </span>
                ) : (
                  <button
                    onClick={() => apply.mutate({ clanName: c.name })}
                    disabled={s === 'pending'}
                    className={cn('btn-primary shrink-0 disabled:opacity-50', MORPH)}
                  >
                    {s === 'pending' ? '…' : 'Apply'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// ── Schedule Match (leader, inline) ─────────────────────────────────────────

function defaultDayTime(day: Date, hour = 19): string {
  // Build a yyyy-mm-ddThh:mm string for the datetime-local input using local tz.
  const d = new Date(day);
  d.setHours(hour, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ScheduleMatchInline({
  day, myClan, onDone,
}: {
  day: Date;
  myClan: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const { data: comps = [] } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [compId, setCompId] = useState('');
  const [opponent, setOpponent] = useState('');
  const [timeStr, setTimeStr] = useState(() => defaultDayTime(day));
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Default to first LIVE comp
  useEffect(() => {
    if (!compId && comps.length > 0) {
      const first = comps.find(c => c.status === 'LIVE') ?? comps[0];
      setCompId(first.id);
    }
  }, [comps, compId]);

  const { data: clans = [] } = useQuery({
    queryKey: ['compClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: !!compId,
  });

  const opponents = clans.filter(c => c.name !== myClan);

  const schedule = useMutation({
    mutationFn: () => api.startMatch({
      compId,
      opponentClan: opponent,
      scheduledAt: new Date(timeStr).getTime(),
    }),
    onSuccess: () => {
      setOk(true);
      qc.invalidateQueries({ queryKey: ['matches', compId] });
      setTimeout(onDone, 1200);
    },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed'),
  });

  const valid = compId && opponent && timeStr && !schedule.isPending;

  if (ok) {
    return <p className="font-mono text-xs text-emerald-300">Match scheduled. Closing…</p>;
  }

  return (
    <form
      onSubmit={e => { e.preventDefault(); setErr(null); if (valid) schedule.mutate(); }}
      className="space-y-3"
    >
      <Field label="Competition">
        <select
          value={compId}
          onChange={e => { setCompId(e.target.value); setOpponent(''); }}
          className="input"
        >
          {comps.length === 0 && <option value="">(no comps available)</option>}
          {comps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Opponent">
        <select
          value={opponent}
          onChange={e => setOpponent(e.target.value)}
          className="input"
          disabled={!compId}
        >
          <option value="">Pick a clan…</option>
          {opponents.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Time">
        <input
          type="datetime-local"
          value={timeStr}
          onChange={e => setTimeStr(e.target.value)}
          className="input"
        />
      </Field>

      {err && <p className="font-mono text-xs text-red-400">{err}</p>}

      <button
        type="submit"
        disabled={!valid}
        className={cn('btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50', MORPH)}
      >
        {schedule.isPending ? 'Scheduling…' : 'Schedule match'}
      </button>
    </form>
  );
}

// ── Create Match (admin/founder, inline) ─────────────────────────────────────

function CreateMatchInline({ day, onDone }: { day: Date; onDone: () => void }) {
  const qc = useQueryClient();
  const { data: comps = [] } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [compId, setCompId] = useState('');
  const [clan1, setClan1] = useState('');
  const [clan2, setClan2] = useState('');
  const [timeStr, setTimeStr] = useState(() => defaultDayTime(day));
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!compId && comps.length > 0) {
      const first = comps.find(c => c.status === 'LIVE') ?? comps[0];
      setCompId(first.id);
    }
  }, [comps, compId]);

  const { data: clans = [] } = useQuery({
    queryKey: ['compClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: !!compId,
  });

  const create = useMutation({
    mutationFn: () => {
      // Match-team-autofill (Lisa, 2026-07-13): thread each side's
      // clanId/teamNo through as an explicit pointer when that side is a
      // specific entered TEAM (not a legacy whole-clan row) — see
      // admin.createMatch's doc comment. Never derived by parsing
      // clan1/clan2 (the display names) apart.
      const c1 = clans.find(c => c.name === clan1);
      const c2 = clans.find(c => c.name === clan2);
      return api.admin.createMatch(compId, clan1, clan2, new Date(timeStr).getTime(), undefined, {
        ...(c1?.teamNo != null ? { clan1ClanId: c1.clanId, clan1TeamNo: c1.teamNo } : {}),
        ...(c2?.teamNo != null ? { clan2ClanId: c2.clanId, clan2TeamNo: c2.teamNo } : {}),
      });
    },
    onSuccess: () => {
      setOk(true);
      qc.invalidateQueries({ queryKey: ['matches', compId] });
      setTimeout(onDone, 1200);
    },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed'),
  });

  const valid = compId && clan1 && clan2 && clan1 !== clan2 && timeStr && !create.isPending;

  if (ok) {
    return <p className="font-mono text-xs text-emerald-300">Match created. Closing…</p>;
  }

  return (
    <form
      onSubmit={e => { e.preventDefault(); setErr(null); if (valid) create.mutate(); }}
      className="space-y-3"
    >
      <Field label="Competition">
        <select
          value={compId}
          onChange={e => { setCompId(e.target.value); setClan1(''); setClan2(''); }}
          className="input"
        >
          {comps.length === 0 && <option value="">(no comps available)</option>}
          {comps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Clan 1">
          <select
            value={clan1}
            onChange={e => setClan1(e.target.value)}
            className="input"
            disabled={!compId}
          >
            <option value="">Pick…</option>
            {clans.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Clan 2">
          <select
            value={clan2}
            onChange={e => setClan2(e.target.value)}
            className="input"
            disabled={!compId}
          >
            <option value="">Pick…</option>
            {clans.filter(c => c.name !== clan1).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Time">
        <input
          type="datetime-local"
          value={timeStr}
          onChange={e => setTimeStr(e.target.value)}
          className="input"
        />
      </Field>

      {clan1 && clan2 && clan1 === clan2 && (
        <p className="font-mono text-xs text-red-400">Clan can't play itself.</p>
      )}
      {err && <p className="font-mono text-xs text-red-400">{err}</p>}

      <button
        type="submit"
        disabled={!valid}
        className={cn('btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50', MORPH)}
      >
        {create.isPending ? 'Creating…' : 'Create match'}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-ink-meta">
        {label}
      </span>
      {children}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CTA Bar — max 5 primary actions, role-gated
// ─────────────────────────────────────────────────────────────────────────────

type CTA = { label: string; to: string; icon: typeof Swords; tone?: 'primary' | 'default' };

function ctasFor(role: Role): CTA[] {
  if (role === 'founder') return [
    { label: 'Onboard Client',  to: '/admin/onboard', icon: Rocket, tone: 'primary' },
    { label: 'Orgs',            to: '/orgs', icon: Building2 },
    { label: 'Activity Log',    to: '/admin/activity', icon: Activity },
    { label: 'Alerts',          to: '/admin/alerts', icon: ShieldAlert },
    { label: 'User Lookup',     to: '/admin/user-lookup', icon: Search },
  ];
  if (role === 'admin') return [
    { label: 'Create Match',  to: '/admin/matches/new', icon: Swords, tone: 'primary' },
    { label: 'Setup',         to: '/admin/setup', icon: Rocket },
    { label: 'Manage Clans',  to: '/admin/clans', icon: Users },
    { label: 'My Org',        to: '/orgs', icon: Building2 },
    { label: 'Admin',         to: '/admin', icon: LayoutDashboard },
  ];
  if (role === 'leader') return [
    { label: 'My Clan',        to: '/leader', icon: Crown, tone: 'primary' },
    { label: 'Schedule Match', to: '/leader', icon: Swords },
    { label: 'Browse Clans',   to: '/clans/browse', icon: Users },
    { label: 'Standings',      to: '/standings', icon: Trophy },
    { label: 'How It Works',   to: '/how-it-works', icon: HelpCircle },
  ];
  if (role === 'member') return [
    { label: 'Find a Clan',    to: '/clans/browse', icon: Users, tone: 'primary' },
    { label: 'My Card',        to: '/player/me', icon: UserCircle2 },
    { label: 'Standings',      to: '/standings', icon: Trophy },
    { label: 'Matches',        to: '/matches', icon: Swords },
    { label: 'How It Works',   to: '/how-it-works', icon: HelpCircle },
  ];
  // public
  return [
    { label: 'Join GG Anvil',  to: '/how-it-works', icon: Rocket, tone: 'primary' },
    { label: 'Standings',      to: '/standings', icon: Trophy },
    { label: 'Matches',        to: '/matches', icon: Swords },
    { label: 'Browse Clans',   to: '/clans/browse', icon: Users },
    { label: 'How It Works',   to: '/how-it-works', icon: HelpCircle },
  ];
}

function CTABar({ role }: { role: Role }) {
  const ctas = ctasFor(role);
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 bg-paper-panel shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.6)] md:sticky md:inset-x-auto md:bottom-auto md:rounded-lg md:bg-paper-elevated md:shadow-none">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-stretch gap-2 px-3 py-3 md:flex-nowrap md:overflow-x-auto">
        {ctas.map(cta => {
          const Icon = cta.icon;
          const primary = cta.tone === 'primary';
          return (
            <Link
              key={cta.label}
              to={cta.to}
              className={cn(
                'flex grow basis-[calc(50%-0.25rem)] items-center justify-center gap-2 rounded-md px-4 py-2.5 font-display text-[11px] font-black uppercase tracking-[0.18em] transition-colors md:grow-0 md:basis-auto md:shrink-0 md:justify-start',
                MORPH,
                primary
                  ? 'bg-accent-500 text-[#0a0a0c] hover:bg-accent-400'
                  : 'bg-white/[0.05] text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary'
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              {cta.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
